<script context="module" lang="ts">
    import { writable } from "svelte/store"

    export const audioPlayerHeight = writable(0)
</script>

<script lang="ts">
    import { audioItem, error, loading, stop, waiting } from "@utils/audiobook/audiobook"
    import { footerHeight } from "@components/Footer.module"
    import { fly } from "svelte/transition"
    import { onDestroy } from "svelte"
    import Icon from "@components/material/Icon.svelte"
    import Controls from "./Controls.svelte"
    import { clickEnter } from "@utils/directives/accessibility"
    import { cssObserver, resizeEntryBorderBoxBlockSize } from "@utils/directives/useCSSObserver"

    export let contextLabel = "Audio"
    export let expanded = false
    export let bottomOffset: string | null = null
    export let bottomClearance = "0.65rem"
    export let bottomInset = "0rem"
    export let focusHidden = false

    let lastAudioItem = $audioItem

    $: if ($audioItem !== lastAudioItem) {
        expanded = false
        lastAudioItem = $audioItem
    }

    $: busy = $loading || $waiting
    $: if (!$audioItem) audioPlayerHeight.set(0)
    $: resolvedBottomOffset = bottomOffset ?? `${$footerHeight}px`

    onDestroy(() => {
        audioPlayerHeight.set(0)
    })

    function close() {
        expanded = false
        stop()
    }
</script>

{#if $audioItem}
    <section
        class="audio-player"
        class:expanded
        class:focus-hidden={focusHidden}
        style={`--audio-player-bottom-offset: ${resolvedBottomOffset}; --audio-player-bottom-clearance: ${bottomClearance}; --audio-player-bottom-inset: ${bottomInset};`}
        aria-label={`${contextLabel} player`}
        aria-hidden={focusHidden}
        transition:fly={{ y: 18, duration: 180 }}
    >
        <div
            class="audio-player-card"
            use:cssObserver={{
                store: audioPlayerHeight,
                fn: resizeEntryBorderBoxBlockSize,
            }}
        >
            <div class="audio-player-progress" aria-hidden="true" />

            <div class="track-art" aria-hidden="true">
                {#if busy}
                    <Icon icon="loader" />
                {:else}
                    <Icon icon="speaker" />
                {/if}
            </div>

            {#if $error}
                <div class="track-copy">
                    <span class="track-kicker">{contextLabel}</span>
                    <strong>Audio unavailable</strong>
                </div>
                <button
                    type="button"
                    class="icon-button close-button"
                    aria-label="Close audio player"
                    on:keydown={clickEnter}
                    on:click={close}
                >
                    <Icon icon="close-sm" />
                </button>
            {:else}
                <div class="track-copy">
                    <span class="track-kicker">{$audioItem.prefix || contextLabel}</span>
                    <strong>{$audioItem.title}</strong>
                </div>

                <Controls {expanded} />

                <div class="audio-actions">
                    <button
                        type="button"
                        class="icon-button expand-button"
                        aria-label={expanded ? "Collapse audio player" : "Expand audio player"}
                        aria-expanded={expanded}
                        on:keydown={clickEnter}
                        on:click={() => (expanded = !expanded)}
                    >
                        <Icon icon="arrow" />
                    </button>
                    <button
                        type="button"
                        class="icon-button close-button"
                        aria-label="Close audio player"
                        on:keydown={clickEnter}
                        on:click={close}
                    >
                        <Icon icon="close-sm" />
                    </button>
                </div>
            {/if}
        </div>
    </section>
{/if}

<style>
    .audio-player {
        --audio-player-surface: var(--reader-dock-backdrop);
        --audio-player-border: var(--reader-dock-border);
        --audio-player-text: var(--reader-dock-text);
        --audio-player-muted: var(--reader-dock-muted-text);
        --audio-player-accent: var(--reader-dock-accent);
        --audio-player-accent-text: var(--reader-dock-accent-text);
        --audio-player-accent-soft: var(--reader-dock-accent-soft);
        --audio-player-active-bottom-inset: var(--audio-player-bottom-inset, 0rem);

        position: fixed;
        right: 0;
        bottom: calc(
            var(--audio-player-bottom-offset, 0px) + var(--audio-player-bottom-clearance, 0rem)
        );
        left: 0;
        z-index: 7;
        pointer-events: none;
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
        transition:
            transform 0.24s ease,
            opacity 0.18s ease,
            visibility 0s;
    }

    .audio-player.focus-hidden {
        opacity: 0;
        visibility: hidden;
        transform: translateY(calc(100% + var(--safe-area-inset-bottom, 0rem) + 1rem));
        transition:
            transform 0.24s ease,
            opacity 0.18s ease,
            visibility 0s 0.24s;
    }

    .audio-player-card {
        position: relative;
        display: grid;
        grid-template-columns: 2.65rem minmax(0, 1fr) auto auto;
        gap: 0.75rem;
        align-items: center;
        width: 100%;
        min-height: calc(4.65rem + var(--audio-player-active-bottom-inset));
        margin: 0;
        padding: 0.65rem max(1rem, var(--safe-area-inset-right, 0rem))
            calc(0.65rem + var(--audio-player-active-bottom-inset))
            max(1rem, var(--safe-area-inset-left, 0rem));
        border-top: 1px solid var(--audio-player-border);
        background: var(--audio-player-surface);
        box-shadow: none;
        color: var(--audio-player-text);
        overflow: hidden;
        pointer-events: auto;
    }

    @media (prefers-reduced-motion: reduce) {
        .audio-player,
        .audio-player.focus-hidden {
            transition: none;
        }
    }

    .audio-player.expanded .audio-player-card {
        grid-template-columns: 2.9rem minmax(0, 1fr) auto;
        grid-template-areas:
            "art copy actions"
            "controls controls controls";
        gap: 0.85rem;
        min-height: calc(9.75rem + var(--audio-player-active-bottom-inset));
        padding-top: 0.85rem;
        padding-bottom: calc(0.85rem + var(--audio-player-active-bottom-inset));
    }

    .audio-player-progress {
        display: none;
    }

    .track-art {
        display: grid;
        place-items: center;
        width: 2.65rem;
        height: 2.65rem;
        border-radius: 0.5rem;
        background: var(--audio-player-accent-soft);
        color: var(--audio-player-accent);
    }

    .track-art :global(svg) {
        width: 1.2rem;
        height: 1.2rem;
    }

    .track-copy {
        display: grid;
        gap: 0.12rem;
        min-width: 0;
    }

    .track-kicker,
    .track-copy strong {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .track-kicker {
        color: var(--audio-player-muted);
        font-size: 0.72rem;
        font-weight: 800;
        letter-spacing: 0.03em;
        line-height: 1;
        text-transform: uppercase;
    }

    .track-copy strong {
        color: var(--audio-player-text);
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-size: 1rem;
        font-weight: 500;
        line-height: 1.2;
        letter-spacing: 0;
    }

    .audio-actions {
        display: flex;
        gap: 0.35rem;
        align-items: center;
    }

    .icon-button {
        appearance: none;
        display: grid;
        place-items: center;
        width: 2.35rem;
        height: 2.35rem;
        border: 0;
        border-radius: 0.55rem;
        background: transparent;
        box-shadow: none;
        color: var(--audio-player-muted);
        cursor: pointer;
        padding: 0;
        transition:
            background-color 0.16s ease,
            color 0.16s ease,
            transform 0.16s ease;
    }

    .icon-button:hover {
        background: var(--audio-player-accent-soft);
        color: var(--audio-player-accent);
    }

    .icon-button:active {
        transform: translateY(1px);
    }

    .icon-button:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }

    .icon-button :global(svg) {
        width: 1rem;
        height: 1rem;
    }

    .expand-button :global(svg) {
        transform: rotate(90deg);
        transition: transform 0.16s ease;
    }

    .audio-player.expanded .expand-button :global(svg) {
        transform: rotate(-90deg);
    }

    .audio-player.expanded .track-art {
        grid-area: art;
        width: 2.9rem;
        height: 2.9rem;
    }

    .audio-player.expanded .track-copy {
        grid-area: copy;
    }

    .audio-player.expanded .audio-actions {
        grid-area: actions;
    }

    .audio-player.expanded :global(.audio-controls) {
        grid-area: controls;
    }

    @media only screen and (min-width: 1154px) {
        .audio-player {
            --audio-player-active-bottom-inset: 0rem;

            right: 0;
            bottom: 0;
            left: var(--app-navigation-width, 0rem);
        }

        .audio-player-card {
            padding-right: 1.5rem;
            padding-left: 1.5rem;
        }
    }

    @media only screen and (max-width: 520px) {
        .audio-player {
            right: 0;
            bottom: calc(
                var(--audio-player-bottom-offset, 0px) + var(--audio-player-bottom-clearance, 0rem)
            );
            left: 0;
        }

        .audio-player-card {
            grid-template-columns: 2.45rem minmax(0, 1fr) auto auto;
            gap: 0.55rem;
            min-height: calc(4.35rem + var(--audio-player-active-bottom-inset));
            padding: 0.6rem max(0.75rem, var(--safe-area-inset-right, 0rem))
                calc(0.6rem + var(--audio-player-active-bottom-inset))
                max(0.75rem, var(--safe-area-inset-left, 0rem));
        }

        .audio-player:not(.expanded) .audio-actions {
            gap: 0.15rem;
        }

        .audio-player:not(.expanded) .icon-button:not(.expand-button):not(.close-button) {
            display: none;
        }

        .audio-player:not(.expanded) :global(.audio-controls .time) {
            display: none;
        }
    }
</style>
